"use client";

import React from "react";
import { ActionButton, ActionButtonTheme, mergeTheme } from "@repo/ui/v2";
import {
  primaryButtonTheme,
  secondaryButtonTheme,
  ghostButtonTheme,
} from "src/components/theme";

/**
 * OwletActionButton Component
 *
 * Owlet-branded wrapper around the base ActionButton component with pre-configured
 * themes and simplified API. Provides primary, secondary, and ghost variants with
 * Owlet's color palette and styling.
 *
 * Features:
 * - Pre-configured Owlet themes (primary: mint green, secondary: transparent with mint text, ghost: transparent with light text)
 * - Automatic opacity handling for disabled primary buttons
 * - Support for both onClick and legacy onPress handlers
 * - Theme customization while maintaining brand consistency
 * - Full ActionButton API compatibility
 *
 * @param text - Button text content
 * @param children - Button content (alternative to text)
 * @param variant - Button style variant (primary, secondary, ghost)
 * @param onClick - Click handler function
 * @param onPress - Legacy click handler for backward compatibility
 * @param loading - Whether button shows loading state
 * @param loadingText - Text to show during loading
 * @param disabled - Whether button is disabled
 * @param transparent - Legacy prop for transparency (deprecated)
 * @param icon - Icon element to display
 * @param fullWidth - Whether button spans full width
 * @param type - HTML button type
 * @param form - Form ID for submit buttons
 * @param theme - Theme overrides for customization
 * @param className - Additional CSS classes
 * @param ariaLabel - Accessibility label
 * @param dataTestId - Test identifier
 * @param eventData - Analytics event data
 * @param customEventName - Custom analytics event name
 */
interface OwletActionButtonProps {
  text?: string;
  children?: React.ReactNode;
  onClick?: () => void;
  onPress?: () => void;
  variant?: "primary" | "secondary" | "ghost";
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  transparent?: boolean;
  icon?: React.ReactNode;
  fullWidth?: boolean;
  type?: "button" | "submit" | "reset";
  form?: string;
  theme?: Partial<ActionButtonTheme>;
  className?: string;
  ariaLabel?: string;
  dataTestId?: string;
  eventData?: Record<string, string | number | boolean>;
  customEventName?: string;
  leftAlign?: boolean;
}

export function OwletActionButton({
  text,
  children,
  variant = "primary",
  transparent: _transparent = false,
  theme,
  className,
  disabled,
  onClick,
  onPress,
  eventData: _eventData,
  customEventName: _customEventName,
  leftAlign = false,
  ...props
}: OwletActionButtonProps) {
  // Select the base theme based on variant
  const baseTheme =
    variant === "secondary"
      ? secondaryButtonTheme
      : variant === "ghost"
        ? ghostButtonTheme
        : primaryButtonTheme;

  // Merge with any custom theme overrides
  const customTheme = theme ? mergeTheme(baseTheme, theme) : baseTheme;

  // Handle left alignment by creating a new theme object (don't mutate!)
  let finalTheme = customTheme;
  if (leftAlign && customTheme.base) {
    // Create a new theme object with modified base class
    const modifiedBase =
      customTheme.base
        // Remove any justify-center or justify-end
        .replace(/\bjustify-(center|end)\b/g, "")
        // Remove all pl-* classes (pl-0, pl-4, etc)
        .replace(/\bpl-\d*\b/g, "")
        // Remove duplicate spaces
        .replace(/\s+/g, " ")
        .trim() + " justify-start pl-0";

    finalTheme = { ...customTheme, base: modifiedBase };
  }

  // Apply transparency class if needed
  // Don't apply opacity here - let the theme handle disabled state
  const finalClassName = className;

  // Use onPress if provided, otherwise use onClick
  const handleClick = onPress || onClick;

  // Use children if provided, otherwise use text
  const content = children || text;

  return (
    <ActionButton
      {...props}
      onClick={handleClick}
      disabled={disabled}
      theme={finalTheme}
      className={finalClassName}
      type={props.type}
      form={props.form}
    >
      {content}
    </ActionButton>
  );
}

export default OwletActionButton;
